import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import Decimal from "decimal.js";
export { formatCurrency, formatUnitPrice } from "@forgepoint/shared";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Returns the matching value from a string-enum value list, or undefined when
 * the input is not one of the values.
 */
export function parseEnumValue<T extends string>(
  values: readonly T[],
  value: string,
): T | undefined {
  return values.find((candidate) => candidate === value);
}

export function formatPhoneNumber(
  phoneNumber: string | undefined | null,
): string {
  if (!phoneNumber) {
    return "";
  }

  const cleaned = phoneNumber.replace(/\D/g, "");

  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  if (cleaned.length === 11) {
    return `+${cleaned[0]} ${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  return phoneNumber;
}

export const keyToLabel = (input: string): string => {
  if (!input) {
    return "";
  }

  const spaced = input
    // snake/kebab → spaces
    .replace(/[_-]+/g, " ")
    // ABCDef → ABC Def  (acronym followed by normal word)
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    // fooBar → foo Bar; 9X → 9 X
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    // collapse / trim
    .replace(/\s+/g, " ")
    .trim();

  // Capitalize words unless they're acronyms (2+ caps) or numbers
  return spaced
    .split(" ")
    .map((w) =>
      /^(?:[A-Z]{2,}|\d+)$/.test(w)
        ? w // keep KING/HTTP/ID/2 as-is
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(" ");
};

/** Formats a weight value to 2 decimal places. */
export const formatWeight = (weight: number | string | null) => {
  if (weight === null || weight === undefined) {
    return "-";
  }
  const weightNumber =
    typeof weight === "string" ? stringToNumber(weight) : weight;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(weightNumber);
};

export const stringToNumber = (amount: string) => {
  return new Decimal(amount).toNumber();
};

/**
 * Formats a file size in bytes to a human-readable string.
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g. "1.2 MB"), or empty string / dash for missing values
 */
export const formatFileSize = (bytes: number | undefined | null): string => {
  if (bytes === undefined || bytes === null) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
